import React, { useEffect, useRef } from "react";

/**
 * Global ambient "DNA lines" — a fixed full-screen canvas with very low
 * density particles and connecting lines. Sits behind all content and
 * shows through semi-transparent section backgrounds for continuity.
 */
const GlobalDNA = () => {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        let raf;
        let particles = [];

        const init = () => {
            const { innerWidth: w, innerHeight: h } = window;
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.scale(dpr, dpr);
            const count = Math.max(35, Math.floor(w * h * 0.00006));
            particles = Array.from({ length: count }).map(() => ({
                x: Math.random() * w,
                y: Math.random() * h,
                vx: (Math.random() - 0.5) * 0.18,
                vy: (Math.random() - 0.5) * 0.18,
                r: Math.random() * 1.3 + 0.4,
                a: Math.random() * 0.35 + 0.15,
                tw: Math.random() * Math.PI * 2,
            }));
        };

        const tick = () => {
            const w = window.innerWidth;
            const h = window.innerHeight;
            ctx.clearRect(0, 0, w, h);

            // connecting lines
            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];
                p.x += p.vx;
                p.y += p.vy;
                p.tw += 0.012;
                if (p.x < 0) p.x = w;
                if (p.x > w) p.x = 0;
                if (p.y < 0) p.y = h;
                if (p.y > h) p.y = 0;
                for (let j = i + 1; j < particles.length; j++) {
                    const q = particles[j];
                    const dx = p.x - q.x;
                    const dy = p.y - q.y;
                    const d2 = dx * dx + dy * dy;
                    if (d2 < 20000) {
                        const alpha = (1 - d2 / 20000) * 0.12;
                        ctx.strokeStyle = `rgba(0,229,255,${alpha})`;
                        ctx.lineWidth = 0.6;
                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(q.x, q.y);
                        ctx.stroke();
                    }
                }
            }

            // dots
            for (const p of particles) {
                const tw = (Math.sin(p.tw) + 1) / 2;
                const alpha = p.a * (0.5 + tw * 0.5);
                ctx.fillStyle = `rgba(124,170,255,${alpha})`;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fill();
            }
            raf = requestAnimationFrame(tick);
        };

        init();
        tick();
        const onResize = () => init();
        window.addEventListener("resize", onResize);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", onResize);
        };
    }, []);

    return (
        <canvas
            ref={ref}
            aria-hidden
            data-testid="global-dna"
            className="fixed inset-0 w-full h-full pointer-events-none z-0"
        />
    );
};

export default GlobalDNA;
